import { useEffect, useState } from "react";
import DashboardLayout, { StatCard, EmptyState } from "./DashboardLayout";
import {
    LayoutDashboard, BookOpen, Users, Contact, FileText, ScrollText, IndianRupee,
    TrendingUp, GraduationCap, Award, Plus, Trash2, Loader2, Pencil,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";

const NAV = [
    { key: "overview", label: "Dashboard", icon: LayoutDashboard },
    { key: "programs", label: "Programs (CMS)", icon: BookOpen },
    { key: "leads", label: "Leads / CRM", icon: Contact },
    { key: "blog", label: "Blog / Content", icon: FileText },
    { key: "users", label: "Users", icon: Users },
    { key: "audit", label: "Audit Logs", icon: ScrollText },
];

const LEAD_STATUSES = ["new", "contacted", "qualified", "counseling", "proposal", "enrolled", "lost"];
const BAR_COLORS = ["#4F46E5", "#F59E0B", "#10B981", "#0F172A", "#0EA5E9"];

export default function OwnerDashboard() {
    const [tab, setTab] = useState("overview");
    const [analytics, setAnalytics] = useState(null);
    const [programs, setPrograms] = useState([]);
    const [leads, setLeads] = useState([]);
    const [blog, setBlog] = useState([]);
    const [users, setUsers] = useState([]);
    const [logs, setLogs] = useState([]);
    const [progDialog, setProgDialog] = useState(null);
    const [blogDialog, setBlogDialog] = useState(null);

    const loadAnalytics = () => api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    const loadPrograms = () => api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
    const loadLeads = () => api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
    const loadBlog = () => api.get("/blog").then((r) => setBlog(r.data)).catch(() => {});
    const loadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    const loadLogs = () => api.get("/admin/audit-logs").then((r) => setLogs(r.data)).catch(() => {});

    useEffect(() => { loadAnalytics(); loadPrograms(); }, []);
    useEffect(() => {
        if (tab === "leads") loadLeads();
        if (tab === "blog") loadBlog();
        if (tab === "users") loadUsers();
        if (tab === "audit") loadLogs();
    }, [tab]);

    const saveProgram = async () => {
        const p = progDialog;
        const payload = { title: p.title, description: p.description, price: p.price, price_value: Number(p.price_value) || 0, duration: p.duration, badge: p.badge, level: p.level, age_group: p.age_group };
        try {
            if (p.id) await api.put(`/admin/programs/${p.id}`, payload);
            else await api.post("/admin/programs", payload);
            toast.success("Program saved."); setProgDialog(null); loadPrograms(); loadAnalytics();
        } catch { toast.error("Could not save program."); }
    };
    const deleteProgram = async (id) => { try { await api.delete(`/admin/programs/${id}`); toast.success("Program deleted."); loadPrograms(); } catch { toast.error("Delete failed."); } };

    const saveBlog = async () => {
        const b = blogDialog;
        const payload = { title: b.title, excerpt: b.excerpt, body: b.body, category: b.category || "Learning", read_time: b.read_time || "5 min", cover: b.cover || "" };
        try {
            if (b.id) await api.put(`/admin/blog/${b.id}`, payload);
            else await api.post("/admin/blog", payload);
            toast.success("Article saved."); setBlogDialog(null); loadBlog();
        } catch { toast.error("Could not save article."); }
    };
    const deleteBlog = async (id) => { try { await api.delete(`/admin/blog/${id}`); toast.success("Article deleted."); loadBlog(); } catch { toast.error("Delete failed."); } };

    const updateLead = async (id, status) => { try { await api.put(`/admin/leads/${id}`, { status }); toast.success("Lead updated."); loadLeads(); loadAnalytics(); } catch { toast.error("Update failed."); } };

    return (
        <DashboardLayout nav={NAV} active={tab} onNav={setTab} title="Owner / Super Admin">
            {tab === "overview" && (!analytics ? <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> : (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard icon={Users} label="Students" value={analytics.total_students} />
                        <StatCard icon={IndianRupee} label="Revenue" value={`₹${analytics.revenue.toLocaleString()}`} color="success" />
                        <StatCard icon={Contact} label="Leads" value={analytics.leads} sub={`${analytics.conversion}% conversion`} color="accent" />
                        <StatCard icon={Award} label="Certificates" value={analytics.certificates} />
                        <StatCard icon={BookOpen} label="Programs" value={analytics.programs_count} />
                        <StatCard icon={GraduationCap} label="Enrollments" value={analytics.enrollments} color="accent" />
                        <StatCard icon={Users} label="Teachers" value={analytics.teachers} />
                        <StatCard icon={TrendingUp} label="Community Reached" value={analytics.community_reached.toLocaleString()} color="success" />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card className="p-6">
                            <h3 className="font-heading font-bold mb-4">Program Performance</h3>
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={analytics.program_performance} margin={{ left: -20 }}>
                                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Bar dataKey="enrollments" radius={[6, 6, 0, 0]}>
                                        {analytics.program_performance.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>
                        <Card className="p-6">
                            <h3 className="font-heading font-bold mb-4">Lead Funnel</h3>
                            <div className="space-y-2.5">
                                {LEAD_STATUSES.map((s) => {
                                    const v = analytics.lead_status[s] || 0;
                                    const max = Math.max(...Object.values(analytics.lead_status), 1);
                                    return (
                                        <div key={s} className="flex items-center gap-3">
                                            <span className="w-24 text-sm capitalize text-foreground/60">{s}</span>
                                            <div className="flex-1 h-6 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${(v / max) * 100}%` }} /></div>
                                            <span className="w-6 text-sm font-medium text-right">{v}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                    </div>
                </div>
            ))}

            {tab === "programs" && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center"><h2 className="font-heading text-lg font-bold">Programs</h2><Button className="rounded-full" data-testid="new-program-btn" onClick={() => setProgDialog({ title: "", description: "", price: "₹", price_value: 0, duration: "", badge: "New", level: "", age_group: "" })}><Plus className="mr-1 h-4 w-4" /> New Program</Button></div>
                    <Card className="p-0 overflow-hidden overflow-x-auto">
                        <Table>
                            <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Price</TableHead><TableHead>Duration</TableHead><TableHead>Enrolls</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                            <TableBody>
                                {programs.map((p) => (
                                    <TableRow key={p.id} data-testid={`admin-program-${p.slug}`}>
                                        <TableCell className="font-medium">{p.title}</TableCell>
                                        <TableCell>{p.price}</TableCell>
                                        <TableCell className="text-foreground/60">{p.duration}</TableCell>
                                        <TableCell>{p.enroll_count}</TableCell>
                                        <TableCell className="text-right space-x-1">
                                            <Button size="icon" variant="ghost" data-testid={`edit-program-${p.slug}`} onClick={() => setProgDialog({ ...p })}><Pencil className="h-4 w-4" /></Button>
                                            <Button size="icon" variant="ghost" className="text-destructive" data-testid={`delete-program-${p.slug}`} onClick={() => deleteProgram(p.id)}><Trash2 className="h-4 w-4" /></Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                </div>
            )}

            {tab === "leads" && (
                <Card className="p-0 overflow-hidden overflow-x-auto">
                    <Table>
                        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Interest</TableHead><TableHead>Score</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                        <TableBody>
                            {leads.length ? leads.map((l) => (
                                <TableRow key={l.id} data-testid={`lead-${l.id}`}>
                                    <TableCell><p className="font-medium">{l.name}</p><p className="text-xs text-foreground/50">{l.email}</p></TableCell>
                                    <TableCell className="text-foreground/60">{l.interest || l.program}</TableCell>
                                    <TableCell><Badge variant="secondary" className="rounded-full">{l.lead_score}</Badge></TableCell>
                                    <TableCell>
                                        <Select value={l.status} onValueChange={(v) => updateLead(l.id, v)}>
                                            <SelectTrigger className="w-36 h-9" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </TableCell>
                                </TableRow>
                            )) : <TableRow><TableCell colSpan={4}><EmptyState icon={Contact} title="No leads found" desc="Leads from the website will appear here." /></TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </Card>
            )}

            {tab === "blog" && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center"><h2 className="font-heading text-lg font-bold">Blog</h2><Button className="rounded-full" data-testid="new-blog-btn" onClick={() => setBlogDialog({ title: "", excerpt: "", body: "", category: "Learning", read_time: "5 min", cover: "" })}><Plus className="mr-1 h-4 w-4" /> New Article</Button></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {blog.map((b) => (
                            <Card key={b.id} className="p-5" data-testid={`admin-blog-${b.slug}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div><Badge variant="secondary" className="rounded-full">{b.category}</Badge><h3 className="mt-2 font-heading font-bold">{b.title}</h3><p className="mt-1 text-sm text-foreground/50 line-clamp-2">{b.excerpt}</p></div>
                                    <div className="flex flex-col gap-1">
                                        <Button size="icon" variant="ghost" onClick={() => setBlogDialog({ ...b })}><Pencil className="h-4 w-4" /></Button>
                                        <Button size="icon" variant="ghost" className="text-destructive" data-testid={`delete-blog-${b.slug}`} onClick={() => deleteBlog(b.id)}><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {tab === "users" && (
                <Card className="p-0 overflow-hidden overflow-x-auto">
                    <Table>
                        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Provider</TableHead></TableRow></TableHeader>
                        <TableBody>{users.map((u) => <TableRow key={u.id}><TableCell className="font-medium">{u.name}</TableCell><TableCell className="text-foreground/60">{u.email}</TableCell><TableCell><Badge variant="secondary" className="rounded-full capitalize">{u.role}</Badge></TableCell><TableCell className="text-foreground/50 capitalize">{u.auth_provider}</TableCell></TableRow>)}</TableBody>
                    </Table>
                </Card>
            )}

            {tab === "audit" && (
                logs.length ? (
                    <Card className="p-0 overflow-hidden overflow-x-auto">
                        <Table>
                            <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Action</TableHead><TableHead>Object</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
                            <TableBody>{logs.map((l) => <TableRow key={l.id}><TableCell className="text-foreground/70">{l.user}</TableCell><TableCell><Badge variant="outline" className="rounded-full">{l.action}</Badge></TableCell><TableCell className="font-mono text-xs text-foreground/50">{l.object}</TableCell><TableCell className="text-xs text-foreground/50">{new Date(l.created_at).toLocaleString()}</TableCell></TableRow>)}</TableBody>
                        </Table>
                    </Card>
                ) : <EmptyState icon={ScrollText} title="No audit logs yet" desc="Important admin actions will be recorded here." />
            )}

            {/* Program dialog */}
            <Dialog open={!!progDialog} onOpenChange={(o) => !o && setProgDialog(null)}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="program-dialog">
                    <DialogHeader><DialogTitle className="font-heading">{progDialog?.id ? "Edit" : "New"} Program</DialogTitle></DialogHeader>
                    {progDialog && (
                        <div className="space-y-3">
                            <div><Label>Title</Label><Input data-testid="program-title-input" value={progDialog.title} onChange={(e) => setProgDialog({ ...progDialog, title: e.target.value })} className="mt-1" /></div>
                            <div><Label>Description</Label><Textarea value={progDialog.description} onChange={(e) => setProgDialog({ ...progDialog, description: e.target.value })} className="mt-1" rows={3} /></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Price (display)</Label><Input data-testid="program-price-input" value={progDialog.price} onChange={(e) => setProgDialog({ ...progDialog, price: e.target.value })} className="mt-1" /></div>
                                <div><Label>Price value (₹)</Label><Input type="number" value={progDialog.price_value} onChange={(e) => setProgDialog({ ...progDialog, price_value: e.target.value })} className="mt-1" /></div>
                                <div><Label>Duration</Label><Input value={progDialog.duration} onChange={(e) => setProgDialog({ ...progDialog, duration: e.target.value })} className="mt-1" /></div>
                                <div><Label>Badge</Label><Input value={progDialog.badge} onChange={(e) => setProgDialog({ ...progDialog, badge: e.target.value })} className="mt-1" /></div>
                                <div><Label>Level</Label><Input value={progDialog.level} onChange={(e) => setProgDialog({ ...progDialog, level: e.target.value })} className="mt-1" /></div>
                                <div><Label>Age group</Label><Input value={progDialog.age_group} onChange={(e) => setProgDialog({ ...progDialog, age_group: e.target.value })} className="mt-1" /></div>
                            </div>
                        </div>
                    )}
                    <DialogFooter><Button variant="outline" onClick={() => setProgDialog(null)}>Cancel</Button><Button onClick={saveProgram} data-testid="save-program-btn">Save</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Blog dialog */}
            <Dialog open={!!blogDialog} onOpenChange={(o) => !o && setBlogDialog(null)}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="blog-dialog">
                    <DialogHeader><DialogTitle className="font-heading">{blogDialog?.id ? "Edit" : "New"} Article</DialogTitle></DialogHeader>
                    {blogDialog && (
                        <div className="space-y-3">
                            <div><Label>Title</Label><Input data-testid="blog-title-input" value={blogDialog.title} onChange={(e) => setBlogDialog({ ...blogDialog, title: e.target.value })} className="mt-1" /></div>
                            <div><Label>Category</Label><Input value={blogDialog.category} onChange={(e) => setBlogDialog({ ...blogDialog, category: e.target.value })} className="mt-1" /></div>
                            <div><Label>Cover image URL</Label><Input value={blogDialog.cover} onChange={(e) => setBlogDialog({ ...blogDialog, cover: e.target.value })} className="mt-1" /></div>
                            <div><Label>Excerpt</Label><Textarea value={blogDialog.excerpt} onChange={(e) => setBlogDialog({ ...blogDialog, excerpt: e.target.value })} className="mt-1" rows={2} /></div>
                            <div><Label>Body</Label><Textarea value={blogDialog.body} onChange={(e) => setBlogDialog({ ...blogDialog, body: e.target.value })} className="mt-1" rows={5} /></div>
                        </div>
                    )}
                    <DialogFooter><Button variant="outline" onClick={() => setBlogDialog(null)}>Cancel</Button><Button onClick={saveBlog} data-testid="save-blog-btn">Save</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
